import { Inject, Injectable, Renderer2, RendererFactory2, RendererStyleFlags2 } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { getErrorMessage } from '@digilize/shared/utils/helpers';
import { APP_CONFIG } from '@digilize/shared/utils/tokens/src';
import { TranslocoService } from '@ngneat/transloco';
import { ApiError, AppConfig, SnackbarType } from '@shared/definitions';
import { first, map, of, switchMap } from 'rxjs';

import { SnackbarComponent } from './snackbar/snackbar.component';

export interface SnackbarData {
  type?: SnackbarType;
  msg: string;
  translate: boolean;
  withoutIcon?: boolean;
  requestId?: string;
}

export type SnackbarPositionClass = 'snackbar-bottom';

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  /* to use this snackbars create classes in application global styling (global-classes) */
  private readonly cssClasses = new Map([
    [SnackbarType.Success, 'success-snackbar'],
    [SnackbarType.Information, 'info-snackbar'],
    [SnackbarType.Error, 'error-snackbar'],
  ]);

  private renderer: Renderer2;

  constructor(
    private snackBar: MatSnackBar,
    rendererFactory: RendererFactory2,
    private translocoService: TranslocoService,
    @Inject(APP_CONFIG) private appConfig: AppConfig
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }
  config: MatSnackBarConfig<any> = {
    duration: 2000,
    data: {},
    panelClass: ['snackbar'],
    horizontalPosition: 'left',
    verticalPosition: 'top',
  };

  legacyDefaultConfig: MatSnackBarConfig<any> = {
    duration: 2000,
    data: {},
    panelClass: ['snackbar'],
    horizontalPosition: 'right',
    verticalPosition: 'bottom', // overwriting this wont work cause of global styles for cdk-global-overlay-wrapper,
  };

  openSnackbar(
    snackbarType: SnackbarType,
    config?: MatSnackBarConfig<SnackbarData>,
    positionClass?: SnackbarPositionClass
  ) {
    if (this.appConfig?.withinDialogScrolling) {
      this.openNewSnackbar(snackbarType, config, positionClass);
    } else {
      this.openLegacySnackbar(snackbarType, config, positionClass);
    }
  }
  private openNewSnackbar(
    snackbarType: SnackbarType,
    config?: MatSnackBarConfig<SnackbarData>,
    positionClass?: SnackbarPositionClass
  ) {
    this.snackBar.openFromComponent<SnackbarComponent>(SnackbarComponent, {
      ...this.config,
      panelClass: ['snackbar', this.cssClasses.get(snackbarType)],
      ...config,
      data: {
        ...config?.data,
        type: snackbarType,
      },
    });

    //fixes global styles (legacy projects)
  }
  private openLegacySnackbar(
    snackbarType: SnackbarType,
    config?: MatSnackBarConfig<SnackbarData>,
    positionClass?: SnackbarPositionClass
  ) {
    this.snackBar.openFromComponent<SnackbarComponent>(SnackbarComponent, {
      ...this.legacyDefaultConfig,
      panelClass: ['snackbar', this.cssClasses.get(snackbarType)],
      ...config,
      data: {
        ...config?.data,
        type: snackbarType,
      },
    });

    //fixes global styles (legacy projects)
    const flags = RendererStyleFlags2.DashCase | RendererStyleFlags2.Important;

    const wrappers = Array.from(document.getElementsByClassName('cdk-global-overlay-wrapper'));
    const snackbarOverlayWrapper = wrappers.find((wrapper) => {
      return !!wrapper.querySelector('.mat-mdc-snack-bar-container');
    });

    if (snackbarOverlayWrapper) {
      this.renderer?.setStyle(snackbarOverlayWrapper, 'pointer-events', 'none', flags);
      if (positionClass) {
        const overlayPane = snackbarOverlayWrapper.querySelector('.cdk-overlay-pane');
        this.renderer?.addClass(overlayPane, positionClass);
      }
    }
  }

  openSnackbarWithApiError(apiError: ApiError, duration = 10000): void {
    if (apiError) {
      this.translocoService
        .selectTranslate('alert.error.type.' + apiError.type)
        .pipe(
          switchMap((message1) => {
            if (apiError.type === 'validation_error' && apiError.info) {
              return this.translocoService
                .selectTranslate('alert.error.type.' + apiError.type)
                .pipe(map((message2) => `${message1} ${message2}`));
            } else {
              return of(message1);
            }
          }),
          first()
        )
        .subscribe((message) => {
          const data: SnackbarData = {
            type: SnackbarType.Error,
            msg: this.getMessage(apiError, message),
            withoutIcon: true,
            translate: false,
            requestId: apiError.request_id,
          };
          this.openSnackbar(SnackbarType.Error, { data, duration });
        });
    }
  }

  openSnackbarWithDynamicApiError(apiError: ApiError, duration = 10000): void {
    console.log({ apiError });
    if (apiError) {
      const message = getErrorMessage(apiError, (key) => this.translocoService.translate(key));
      if (message) {
        console.log({ message });
        const data: SnackbarData = {
          type: SnackbarType.Error,
          msg: this.getMessage(apiError, message),
          withoutIcon: true,
          translate: false,
          requestId: apiError.request_id,
        };
        console.log({ data, duration });
        this.openSnackbar(SnackbarType.Error, { data, duration });
      }
    }
  }

  private getMessage(apiError: ApiError, message: string): string {
    if (apiError.type === 'validation_error' && apiError.info?.field) {
      message += ` [field: ${apiError.info.field}]`;
    }

    return message.replace('{{email}}', this.appConfig?.email);
  }
}
